import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Eye, FileText, Pencil, Trash2 } from 'lucide-react';
import EmptyState from '../Common/EmptyState';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';

const thClass =
  'px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 border-b border-gray-200 bg-gray-50';
const tdClass = 'px-3 py-3 text-sm text-gray-900 border-b border-gray-100 align-top';

const SIT_BADGE = {
  1: 'bg-sky-100 text-sky-900',
  2: 'bg-amber-100 text-amber-950',
  3: 'bg-emerald-100 text-emerald-900',
  4: 'bg-slate-100 text-slate-800',
};

export default function ProcessoTable({
  items,
  pagination,
  totalCount,
  situacaoLabels,
  tipoLabels,
  onEdit,
  onDelete,
  onCreate,
  requester,
}) {
  const total = totalCount != null ? totalCount : items?.length ?? 0;
  if (!items?.length) {
    return (
      <div className="card">
        <div className="card-content py-2">
          <EmptyState
            icon={FileText}
            title="Nenhum processo encontrado"
            description={
              requester
                ? 'Quando o órgão abrir um processo a partir do seu requerimento, ele aparece aqui.'
                : 'Ajuste os filtros de busca ou cadastre um novo processo administrativo.'
            }
            action={
              onCreate ? (
                <Can permission={P.PROCESS_ACCESS}>
                  <button type="button" className="btn btn-primary" onClick={onCreate}>
                    Novo processo
                  </button>
                </Can>
              ) : null
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="-mx-5 overflow-x-auto sm:-mx-6">
        <table className="w-full min-w-[48rem] table-fixed border-collapse">
          <colgroup>
            <col className="w-[18%]" />
            <col className="w-[16%]" />
            <col />
            <col className="w-[14%]" />
            <col className="w-[10rem]" />
          </colgroup>
          <thead>
            <tr>
              <th className={thClass}>Número</th>
              <th className={thClass}>Situação</th>
              <th className={thClass}>Tipo</th>
              <th className={thClass}>Abertura</th>
              <th className={`${thClass} sticky right-0 z-20 shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.18)]`}>
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="group hover:bg-gray-50">
                <td className={tdClass}>
                  <Link to={`/process/cases/${item.id}`} className="font-medium text-primary-700 hover:text-primary-900">
                    {item.numero || item.id?.slice(0, 8)}
                  </Link>
                </td>
                <td className={tdClass}>
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      SIT_BADGE[item.situacao] || 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {situacaoLabels[item.situacao] ?? item.situacao}
                  </span>
                </td>
                <td className={`${tdClass} text-gray-700`}>{tipoLabels[item.tipo] ?? item.tipo}</td>
                <td className={`${tdClass} text-gray-600`}>
                  {item.data_abertura ? String(item.data_abertura).slice(0, 10) : '—'}
                </td>
                <td
                  className={`${tdClass} sticky right-0 z-10 bg-white shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.12)] group-hover:bg-gray-50`}
                >
                  <div className="flex justify-end gap-1">
                    <Link
                      to={`/process/cases/${item.id}`}
                      className="btn btn-outline btn-sm btn-icon"
                      title="Consultar"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                    {requester ? null : (
                      <Can permission={P.PROCESS_ACCESS}>
                        <button
                          type="button"
                          onClick={() => onEdit?.(item)}
                          className="btn btn-outline btn-sm btn-icon"
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete?.(item.id)}
                          className="btn btn-outline btn-sm btn-icon text-danger-600 hover:bg-danger-50 hover:text-danger-700"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </Can>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination ? (
        <div className="pagination border-t border-gray-200 px-1 py-4 sm:px-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-600">
              Mostrando {total ? (pagination.page - 1) * pagination.limit + 1 : 0} a{' '}
              {Math.min(pagination.page * pagination.limit, total)} de {total} processos
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={pagination.prevPage}
                disabled={!pagination.hasPrev}
                className="pagination-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: Math.min(5, pagination.totalPages || 1) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    type="button"
                    onClick={() => pagination.goToPage(page)}
                    className={`pagination-btn ${pagination.page === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={pagination.nextPage}
                disabled={!pagination.hasNext}
                className="pagination-btn"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
